import {
  ButtonDropdown,
  ButtonDropdownProps,
  NonCancelableCustomEvent,
} from '@amzn/awsui-components-core';
import { useState, useContext } from 'react';
import { AppContext } from '../../contexts/AppContext';
import { FeatureFlagContext } from '../../contexts/FeatureFlagContext';
import { ActiveRole, useOptionalCurrentUser } from '../../contexts/UserContext';
import constants from '../../common/constants';
import EnvironmentConfig from '../../common/EnvironmentConfig';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import ButtonDropdownItem from '../../model/ButtonDropdownItem';
import '../../resources/styles/scss/components/common/header.css';
import BrowserHistory from '../utils/BrowserHistory';
import { ContentSubType } from '@amzn/awscat-pattern-library-service-model';
import { contentSubTypeDisplayNames } from '../utils/DisplayNames';
import { redirectToUserProfilePage } from '../apgl-user-profile/APGLUserProfileUtils';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import contactUrls from '../../common/contactUrls';

const ROLE_LABELS: Record<ActiveRole, string> = {
  author: 'Author',
  bar_raiser: 'Bar raiser',
  admin: 'Admin',
};

const feedbackItems: ButtonDropdownItem[] = [
  {
    text: constants.CONTACT_US_REPORT_BUG,
    id: constants.CONTACT_US_REPORT_BUG,
  },
  {
    text: constants.CONTACT_US_ADMIN_SUPPORT,
    id: constants.CONTACT_US_ADMIN_SUPPORT,
  },
  {
    text: constants.CONTACT_US_FEAT_REQUEST,
    id: constants.CONTACT_US_FEAT_REQUEST,
  },
  {
    text: constants.CONTACT_US_JOIN_SLACK_CHANNEL,
    id: constants.CONTACT_US_JOIN_SLACK_CHANNEL,
  },
];

const resourcesItems: ButtonDropdownItem[] = [
  {
    id: constants.WHATS_NEW,
    text: constants.WHATS_NEW,
  },
  {
    id: ContentSubType.GettingStarted,
    // @ts-expect-error TS2322 - strict migration
    text: contentSubTypeDisplayNames.get(ContentSubType.GettingStarted),
  },
  {
    id: ContentSubType.ReleaseNotes,
    // @ts-expect-error TS2322 - strict migration
    text: contentSubTypeDisplayNames.get(ContentSubType.ReleaseNotes),
  },
  {
    id: ContentSubType.News,
    // @ts-expect-error TS2322 - strict migration
    text: contentSubTypeDisplayNames.get(ContentSubType.News),
  },
  {
    id: ContentSubType.ArchitectureDiagram,
    // @ts-expect-error TS2322 - strict migration
    text: contentSubTypeDisplayNames.get(ContentSubType.ArchitectureDiagram),
  },
  {
    id: constants.SHARE_GUIDANCE,
    text: constants.SHARE_GUIDANCE,
  },
];

const HeaderNav = () => {
  const appContext = useContext(AppContext);
  const featureFlagContext = useContext(FeatureFlagContext);
  const isUxRedesignEnabled = featureFlagContext?.isFeatureEnabled('UxRedesign') ?? false;
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const { authnInfo } = appContext;
  const currentUser = useOptionalCurrentUser();

  const [isUserProfileButtonLoading, setIsUserProfileButtonLoading] = useState(false);

  const openGettingStarted = () => {
    BrowserHistory.push(RouterPaths.WHATS_NEW, { postType: ContentSubType.GettingStarted });
  };

  const openReleaseNotes = () => {
    BrowserHistory.push(RouterPaths.WHATS_NEW, { postType: ContentSubType.ReleaseNotes });
  };

  const openNews = () => {
    BrowserHistory.push(RouterPaths.WHATS_NEW, { postType: ContentSubType.News });
  };

  const openWhatsNew = () => {
    BrowserHistory.push(RouterPaths.WHATS_NEW, {
      postType: constants.WHATS_NEW_CARD_DEFAULT_POST_TYPES,
    });
  };

  const openShareGuidancePage = () => {
    BrowserHistory.push('/post-viewer/7e37a09f-5b79-4498-a6a1-0190dd303f7d');
  };

  const handleFeedbackClick = (
    event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    const eventId = event.detail.id;
    const url = contactUrls[eventId];
    if (!url) {
      throw new Error(`Menu item with id ${eventId} does not have a url associated with it`);
    }
    window.open(url, '_blank');
  };

  const downloadDiagramDeck = async () => {
    const { url } = await mplServiceClient.getDiagramGuidelinesDeck();
    window.open(url, '_blank');
  };

  const handleResourcesClick = async (
    event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    const eventId = event.detail.id;
    switch (eventId) {
      case constants.WHATS_NEW:
        openWhatsNew();
        break;
      case ContentSubType.GettingStarted:
        openGettingStarted();
        break;
      case ContentSubType.ReleaseNotes:
        openReleaseNotes();
        break;
      case ContentSubType.News:
        openNews();
        break;
      case ContentSubType.ArchitectureDiagram:
        await downloadDiagramDeck();
        break;
      case constants.SHARE_GUIDANCE:
        await openShareGuidancePage();
        break;
    }
  };

  const logOut = () => {
    window.location.replace(`${EnvironmentConfig.getConfig().portalEndpoint}/portal/signout`);
  };

  const openUserProfile = async () => {
    // @ts-expect-error TS2339 - strict migration
    const { userId } = authnInfo?.userProfile;
    setIsUserProfileButtonLoading(true);
    await redirectToUserProfilePage(userId);
    setIsUserProfileButtonLoading(false);
  };

  const handleProfileClick = async (
    event: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    const eventId = event.detail.id;
    if (eventId === constants.LOGOUT) {
      logOut();
    } else if (eventId === constants.APGL_USER_PROFILE) {
      await openUserProfile();
    } else if (eventId.startsWith('role:')) {
      const role = eventId.replace('role:', '') as ActiveRole;
      currentUser?.setActiveRole(role);
    }
  };

  const buildProfileItems = (): ButtonDropdownProps.ItemOrGroup[] => {
    const items: ButtonDropdownProps.ItemOrGroup[] = [
      { id: constants.APGL_USER_PROFILE, text: constants.APGL_USER_PROFILE },
    ];

    if (isUxRedesignEnabled && currentUser && currentUser.availableRoles.length > 1) {
      items.push({
        id: 'role-group',
        text: 'Switch role',
        // Render roles as checkbox items so the active role shows Cloudscape's native check; the blue styling in header.css marks it as the current selection.
        items: currentUser.availableRoles.map((role: ActiveRole) => ({
          id: `role:${role}`,
          text: ROLE_LABELS[role],
          itemType: 'checkbox',
          checked: role === currentUser.activeRole,
        })),
      });
    }

    items.push({ id: constants.LOGOUT, text: constants.LOGOUT });
    return items;
  };

  if (authnInfo) {
    const { userProfile } = authnInfo;
    const userType = ['AWS', 'Partner'].includes(userProfile.type) ? ` (${userProfile.type})` : '';
    const displayName = `${userProfile.name}${userType}`;
    return (
      <div className="header-action-items">
        {/* Items displayed in reverse direction. */}
        <div className="header-nav-item">
          <ButtonDropdown
            items={feedbackItems.map((i) => ({ id: i.id, text: i.text }) as any)}
            onItemClick={handleFeedbackClick}
          >
            {constants.CONTACT_US}
          </ButtonDropdown>
        </div>
        <div className="pipe">|</div>
        <div className="header-nav-item">
          <ButtonDropdown
            items={resourcesItems.map((i) => ({ id: i.id, text: i.text }) as any)}
            onItemClick={handleResourcesClick}
          >
            {constants.RESOURCES}
          </ButtonDropdown>
        </div>
        <div className="pipe">|</div>
        <div className="header-nav-item">
          <ButtonDropdown
            loading={isUserProfileButtonLoading}
            items={buildProfileItems()}
            onItemClick={handleProfileClick}
          >
            {displayName}
          </ButtonDropdown>
        </div>
      </div>
    );
  } else {
    return (
      <div className={'header-nav-container'}>
        {/* Items displayed in reverse direction. */}
        <div className="header-nav-item">
          <ButtonDropdown
            items={resourcesItems.map((i) => ({ id: i.id, text: i.text }) as any)}
            onItemClick={handleResourcesClick}
          >
            {constants.RESOURCES}
          </ButtonDropdown>
        </div>
      </div>
    );
  }
};

export default withAppContext(HeaderNav);
